/**
 * AdminMemoryPage — V9.8
 *
 * CRUD admin du module Mémoire (articles, archives, portraits, événements marquants…).
 *
 * Accès : `admin` ou `copf` uniquement.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Plus, Pencil, Eye, EyeOff, Archive, RotateCcw, Trash2, ArrowLeft, Loader2, X, ImagePlus, Youtube, FileText } from "lucide-react";
import { toast } from "sonner";
import { showError } from "@/lib/errors";
import { extractYouTubeId } from "@/components/media/YouTubeEmbed";

const MEMORY_TYPES = [
  { value: "article", label: "Article" },
  { value: "archive", label: "Archive sportive" },
  { value: "portrait_historique", label: "Portrait historique" },
  { value: "competition_ancienne", label: "Compétition ancienne" },
  { value: "photo_video", label: "Photo / vidéo d'archive" },
  { value: "temoignage", label: "Témoignage" },
  { value: "palmares_historique", label: "Palmarès historique" },
  { value: "evenement_marquant", label: "Événement marquant" },
];

const STATUS_BADGES = {
  draft: { label: "Brouillon", className: "bg-amber-100 text-amber-800" },
  published: { label: "Publié", className: "bg-emerald-100 text-emerald-800" },
  archived: { label: "Archivé", className: "bg-slate-200 text-slate-700" },
};

function MemoryFormDialog({ open, onOpenChange, initial, disciplines, onSaved }) {
  const isEdit = !!initial?.id;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => ({
    title: "", type: "article", short_description: "", content: "",
    discipline: "", year: "", period_end_year: "", source_url: "", image_url: "",
    tags: "",
    media_urls: [],       // galerie images
    youtube_urls: [],     // vidéos YouTube
    documents: [],        // [{url, label, kind}]
  }));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  // Champs dynamiques temporaires pour ajout
  const [galleryInput, setGalleryInput] = useState("");
  const [youtubeInput, setYoutubeInput] = useState("");
  const [docUrlInput, setDocUrlInput] = useState("");
  const [docLabelInput, setDocLabelInput] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        title: initial?.title || "",
        type: initial?.type || "article",
        short_description: initial?.short_description || "",
        content: initial?.content || "",
        discipline: initial?.discipline || "",
        year: initial?.year ?? "",
        period_end_year: initial?.period_end_year ?? "",
        source_url: initial?.source_url || "",
        image_url: initial?.image_url || "",
        tags: (initial?.tags || []).join(", "),
        media_urls: Array.isArray(initial?.media_urls) ? [...initial.media_urls] : [],
        youtube_urls: Array.isArray(initial?.youtube_urls) ? [...initial.youtube_urls] : [],
        documents: Array.isArray(initial?.documents) ? initial.documents.map((d) => ({ ...d })) : [],
      });
      setGalleryInput(""); setYoutubeInput(""); setDocUrlInput(""); setDocLabelInput("");
    }
  }, [open, initial]);

  // ---- helpers d'édition ----
  const addGalleryUrl = () => {
    const u = galleryInput.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) { toast.error("URL image invalide (doit commencer par http/https)."); return; }
    if (form.media_urls.includes(u)) { toast.info("Cette image est déjà dans la galerie."); return; }
    setForm((f) => ({ ...f, media_urls: [...f.media_urls, u] }));
    setGalleryInput("");
  };
  const removeGalleryUrl = (u) => setForm((f) => ({ ...f, media_urls: f.media_urls.filter((x) => x !== u) }));

  const addYoutube = () => {
    const u = youtubeInput.trim();
    if (!u) return;
    if (!extractYouTubeId(u)) { toast.error("Lien YouTube non reconnu."); return; }
    if (form.youtube_urls.includes(u)) { toast.info("Cette vidéo est déjà ajoutée."); return; }
    setForm((f) => ({ ...f, youtube_urls: [...f.youtube_urls, u] }));
    setYoutubeInput("");
  };
  const removeYoutube = (u) => setForm((f) => ({ ...f, youtube_urls: f.youtube_urls.filter((x) => x !== u) }));

  const addDocument = () => {
    const url = docUrlInput.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) { toast.error("URL document invalide (http/https requis)."); return; }
    const label = docLabelInput.trim() || null;
    const lower = url.toLowerCase();
    const kind = lower.endsWith(".pdf") ? "pdf" : (/\.(png|jpe?g|webp|gif)$/.test(lower) ? "image" : "other");
    setForm((f) => ({ ...f, documents: [...f.documents, { url, label, kind }] }));
    setDocUrlInput(""); setDocLabelInput("");
  };
  const removeDocument = (url) => setForm((f) => ({ ...f, documents: f.documents.filter((d) => d.url !== url) }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) {
      toast.error("Le titre est obligatoire.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        period_end_year: form.period_end_year ? Number(form.period_end_year) : null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        // Arrays médias — toujours renvoyés (pour vider si l'admin supprime)
        media_urls: Array.isArray(form.media_urls) ? form.media_urls : [],
        youtube_urls: Array.isArray(form.youtube_urls) ? form.youtube_urls : [],
        documents: Array.isArray(form.documents) ? form.documents : [],
      };
      Object.keys(payload).forEach((k) => {
        // NB: on garde les arrays même vides pour permettre l'effacement
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      if (isEdit) {
        await api.patch(`/memory/${initial.id}`, payload);
        toast.success("Élément Mémoire mis à jour.");
      } else {
        await api.post("/memory", payload);
        toast.success("Élément Mémoire créé.");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e2) {
      showError(e2, { fallback: "Échec de l'enregistrement" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="memory-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifier l'élément Mémoire" : "Nouvel élément Mémoire"}</DialogTitle>
          <DialogDescription className="text-xs">
            Crée un brouillon. Tu pourras le publier ensuite depuis la liste.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="m-title" className="text-xs">Titre *</Label>
              <Input id="m-title" data-testid="memory-title" value={form.title} onChange={set("title")} required />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="memory-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEMORY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="m-short" className="text-xs">Description courte</Label>
            <Textarea id="m-short" data-testid="memory-short-desc" rows={2} value={form.short_description} onChange={set("short_description")} />
          </div>
          <div>
            <Label htmlFor="m-content" className="text-xs">Contenu détaillé (markdown OK)</Label>
            <Textarea id="m-content" data-testid="memory-content" rows={8} value={form.content} onChange={set("content")} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Discipline</Label>
              <Select value={form.discipline || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, discipline: v === "_none" ? "" : v }))}>
                <SelectTrigger data-testid="memory-discipline"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="_none">— Aucune —</SelectItem>
                  {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="m-year" className="text-xs">Année</Label>
              <Input id="m-year" type="number" data-testid="memory-year" value={form.year} onChange={set("year")} placeholder="1985" />
            </div>
            <div>
              <Label htmlFor="m-year-end" className="text-xs">Année fin (si période)</Label>
              <Input id="m-year-end" type="number" data-testid="memory-year-end" value={form.period_end_year} onChange={set("period_end_year")} placeholder="1990" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="m-source" className="text-xs">URL source</Label>
              <Input id="m-source" type="url" data-testid="memory-source-url" value={form.source_url} onChange={set("source_url")} placeholder="https://..." />
            </div>
            <div>
              <Label htmlFor="m-image" className="text-xs">URL image (cover)</Label>
              <Input id="m-image" type="url" data-testid="memory-image-url" value={form.image_url} onChange={set("image_url")} placeholder="https://..." />
            </div>
          </div>

          {/* ---- Galerie images ---- */}
          <div className="rounded-md border p-3 space-y-2 bg-secondary/20">
            <div className="flex items-center gap-1.5 text-xs font-semibold">
              <ImagePlus className="h-3.5 w-3.5" /> Galerie d'images ({form.media_urls.length})
            </div>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://exemple.com/photo.jpg"
                value={galleryInput}
                onChange={(e) => setGalleryInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGalleryUrl(); } }}
                data-testid="memory-gallery-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addGalleryUrl} data-testid="memory-gallery-add">Ajouter</Button>
            </div>
            {form.media_urls.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {form.media_urls.map((u) => (
                  <div key={u} className="relative group rounded overflow-hidden border bg-muted">
                    <img src={u} alt="" className="w-full h-20 object-cover object-top" />
                    <button type="button"
                      onClick={() => removeGalleryUrl(u)}
                      className="absolute top-0.5 right-0.5 rounded-full bg-black/70 p-0.5 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                      aria-label="Retirer l'image"
                      data-testid={`memory-gallery-remove-${form.media_urls.indexOf(u)}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">Les images sont affichées à leur ratio naturel côté public, avec zoom (lightbox) au clic.</p>
          </div>

          {/* ---- Vidéos YouTube ---- */}
          <div className="rounded-md border p-3 space-y-2 bg-secondary/20">
            <div className="flex items-center gap-1.5 text-xs font-semibold">
              <Youtube className="h-3.5 w-3.5 text-red-600" /> Vidéos YouTube ({form.youtube_urls.length})
            </div>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://youtu.be/... ou https://www.youtube.com/watch?v=..."
                value={youtubeInput}
                onChange={(e) => setYoutubeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addYoutube(); } }}
                data-testid="memory-youtube-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addYoutube} data-testid="memory-youtube-add">Ajouter</Button>
            </div>
            {form.youtube_urls.length > 0 && (
              <ul className="space-y-1">
                {form.youtube_urls.map((u, idx) => (
                  <li key={u} className="flex items-center gap-2 text-xs bg-background rounded px-2 py-1 border">
                    <Youtube className="h-3 w-3 text-red-600 shrink-0" />
                    <span className="truncate flex-1 font-mono text-[11px]">{u}</span>
                    <button type="button" onClick={() => removeYoutube(u)}
                      className="text-muted-foreground hover:text-red-700"
                      aria-label="Retirer la vidéo"
                      data-testid={`memory-youtube-remove-${idx}`}
                    ><X className="h-3.5 w-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ---- Documents / PDF ---- */}
          <div className="rounded-md border p-3 space-y-2 bg-secondary/20">
            <div className="flex items-center gap-1.5 text-xs font-semibold">
              <FileText className="h-3.5 w-3.5" /> Documents / PDF ({form.documents.length})
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
              <Input
                placeholder="Libellé (ex: Programme JP 1997)"
                value={docLabelInput}
                onChange={(e) => setDocLabelInput(e.target.value)}
                data-testid="memory-doc-label-input"
              />
              <Input
                type="url"
                placeholder="https://exemple.com/document.pdf"
                value={docUrlInput}
                onChange={(e) => setDocUrlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDocument(); } }}
                data-testid="memory-doc-url-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addDocument} data-testid="memory-doc-add">Ajouter</Button>
            </div>
            {form.documents.length > 0 && (
              <ul className="space-y-1">
                {form.documents.map((d, idx) => (
                  <li key={d.url} className="flex items-center gap-2 text-xs bg-background rounded px-2 py-1 border">
                    <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold truncate block">{d.label || "(sans libellé)"}</span>
                      <span className="truncate block text-muted-foreground font-mono text-[10px]">{d.url}</span>
                    </span>
                    <Badge variant="outline" className="text-[9px]">{d.kind || "other"}</Badge>
                    <button type="button" onClick={() => removeDocument(d.url)}
                      className="text-muted-foreground hover:text-red-700"
                      aria-label="Retirer le document"
                      data-testid={`memory-doc-remove-${idx}`}
                    ><X className="h-3.5 w-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Label htmlFor="m-tags" className="text-xs">Tags (séparés par virgule)</Label>
            <Input id="m-tags" data-testid="memory-tags" value={form.tags} onChange={set("tags")} placeholder="va'a, fenua, anciens..." />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
            <Button type="submit" disabled={busy} data-testid="memory-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              {isEdit ? "Enregistrer" : "Créer le brouillon"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminMemoryPage() {
  const { user } = useAuth();
  const isAdmin = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [disciplines, setDisciplines] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const params = { include_deleted: false, include_archived: true, limit: 200 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/memory/admin", { params });
      setItems(r.data.items || []);
    } catch (e) {
      showError(e, { prefix: "Chargement échoué :" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) reload();
    else setLoading(false);
    // eslint-disable-next-line
  }, [isAdmin, statusFilter]);

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])).catch(() => {});
  }, []);

  const callAction = async (item, action) => {
    try {
      await api.post(`/memory/${item.id}/${action}`);
      toast.success(`Action « ${action} » appliquée à « ${item.title} ».`);
      reload();
    } catch (e) {
      showError(e, { fallback: "Action impossible" });
    }
  };

  const deleteItem = async (item) => {
    if (!confirm(`Supprimer « ${item.title} » ? (récupérable depuis la corbeille admin)`)) return;
    try {
      await api.delete(`/memory/${item.id}`);
      toast.success("Élément Mémoire supprimé.");
      reload();
    } catch (e) {
      showError(e, { fallback: "Suppression impossible" });
    }
  };

  const stats = useMemo(() => ({
    draft: items.filter((i) => i.status === "draft" && !i.archived).length,
    published: items.filter((i) => i.status === "published").length,
    archived: items.filter((i) => i.status === "archived" || i.archived).length,
  }), [items]);

  if (!user) return <div className="p-8"><Card className="p-6 text-center">Connecte-toi pour accéder à cette page.</Card></div>;
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Card className="p-6">
          <Sparkles className="h-6 w-6 text-[hsl(var(--aito-gold))]" />
          <h2 className="font-display text-xl font-bold mt-2">Accès réservé</h2>
          <p className="text-sm text-muted-foreground mt-1">
            La gestion du module Mémoire est réservée aux comptes <code>admin</code> et <code>copf</code>.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-memory-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="memory-back-admin">
        <ArrowLeft className="h-3 w-3" /> Retour Admin
      </Link>
      <div className="flex items-start justify-between gap-3 flex-wrap mt-2">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-[hsl(var(--aito-gold))]" />
            Mémoire — Administration
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gérer les articles, portraits, archives et événements marquants de la mémoire du sport polynésien.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          data-testid="memory-create-trigger"
          className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold))]/80"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Nouvel élément
        </Button>
      </div>

      <Separator className="my-6" />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="p-3"><div className="text-[11px] text-muted-foreground uppercase">Brouillons</div><div className="text-2xl font-display font-bold text-amber-700">{stats.draft}</div></Card>
        <Card className="p-3"><div className="text-[11px] text-muted-foreground uppercase">Publiés</div><div className="text-2xl font-display font-bold text-emerald-700">{stats.published}</div></Card>
        <Card className="p-3"><div className="text-[11px] text-muted-foreground uppercase">Archivés</div><div className="text-2xl font-display font-bold text-slate-700">{stats.archived}</div></Card>
      </div>

      {/* Filtres */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-3">
        <TabsList>
          <TabsTrigger value="all" data-testid="memory-tab-all">Tous</TabsTrigger>
          <TabsTrigger value="draft" data-testid="memory-tab-draft">Brouillons</TabsTrigger>
          <TabsTrigger value="published" data-testid="memory-tab-published">Publiés</TabsTrigger>
          <TabsTrigger value="archived" data-testid="memory-tab-archived">Archivés</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex gap-2 mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (titre, description, tag)…" data-testid="memory-search" onKeyDown={(e) => { if (e.key === "Enter") reload(); }} />
        <Button variant="outline" onClick={reload} data-testid="memory-search-btn">Rechercher</Button>
      </div>

      {/* Liste */}
      {loading ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">Chargement…</Card>
      ) : items.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">Aucun élément publié dans la Mémoire du sport polynésien pour ces filtres.</Card>
      ) : (
        <ul className="space-y-2" data-testid="memory-list">
          {items.map((it) => {
            const badge = STATUS_BADGES[it.status] || STATUS_BADGES.draft;
            const typeLabel = MEMORY_TYPES.find((t) => t.value === it.type)?.label || it.type;
            return (
              <li key={it.id}>
                <Card className="p-3 flex items-start gap-3 aito-card-hover">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-1.5 mb-1">
                      <Badge variant="outline" className="text-[10px]">{typeLabel}</Badge>
                      <Badge className={`text-[10px] ${badge.className}`}>{badge.label}</Badge>
                      {it.year && <Badge variant="outline" className="text-[10px] font-mono">{it.year}{it.period_end_year ? `–${it.period_end_year}` : ""}</Badge>}
                      {it.discipline && <span className="text-[11px] text-muted-foreground capitalize">{it.discipline}</span>}
                    </div>
                    <div className="font-display font-semibold leading-tight">{it.title}</div>
                    {it.short_description && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{it.short_description}</p>}
                    <div className="text-[11px] text-muted-foreground mt-1 font-mono">/memoire/{it.slug}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(it); setFormOpen(true); }} title="Modifier" data-testid={`memory-edit-${it.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {it.status !== "published" ? (
                      <Button size="sm" variant="ghost" onClick={() => callAction(it, "publish")} title="Publier" data-testid={`memory-publish-${it.id}`}>
                        <Eye className="h-3.5 w-3.5 text-emerald-700" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => callAction(it, "unpublish")} title="Dépublier" data-testid={`memory-unpublish-${it.id}`}>
                        <EyeOff className="h-3.5 w-3.5 text-amber-700" />
                      </Button>
                    )}
                    {it.status !== "archived" ? (
                      <Button size="sm" variant="ghost" onClick={() => callAction(it, "archive")} title="Archiver" data-testid={`memory-archive-${it.id}`}>
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => callAction(it, "restore")} title="Restaurer" data-testid={`memory-restore-${it.id}`}>
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteItem(it)} title="Supprimer" data-testid={`memory-delete-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-700" />
                    </Button>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <MemoryFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        disciplines={disciplines}
        onSaved={reload}
      />
    </div>
  );
}
